import React from 'react';
import {
  useNamespacedTranslation,
  TFunc,
} from '../../../../../../../hooks/useNamespacedTranslation';
import FormattedDate from '../../../../../../../components/date/FormattedDate';
import { useEmployeeSalariesForChangeRequestQuery } from '../../../../../__generated__/graphql';
import { frequencyOptions, DEFAULT_CURRENCY } from './types';

export type SalaryChangeData = {
  currentSalaryId?: string;
  newValue: number;
  effectiveDate?: string;
  endDate?: string;
  newFrequency?: string;
  notes?: string;
  isQuickUpdate?: boolean;
};

export type SalaryChangeOverviewProps = {
  salaryChange: SalaryChangeData;
  employeeId?: string;
};

const formatFrequencyForDisplay = (frequency: string, t: TFunc) => {
  const option = frequencyOptions.find((opt) => opt.value === frequency);
  return option ? t('options.' + option.label) : frequency.toLowerCase();
};

export const SalaryChangeOverview: React.FC<SalaryChangeOverviewProps> = ({
  salaryChange,
  employeeId,
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm.salaryChange');

  const { data: salaryData } = useEmployeeSalariesForChangeRequestQuery({
    variables: { employeeId: employeeId || '' },
    skip: !employeeId || !salaryChange?.currentSalaryId,
  });

  const originalSalary = salaryData?.manager?.employee?.salaries?.find(
    (s) => s.Id === salaryChange?.currentSalaryId,
  );

  const displayPreviousSalary = salaryChange?.currentSalaryId &&
    originalSalary && {
      amount: originalSalary.flair__Amount__c,
      frequency: originalSalary.flair__Salary_Type__c,
      currency: originalSalary.CurrencyIsoCode || DEFAULT_CURRENCY,
    };
  const newValueCurrency = originalSalary?.CurrencyIsoCode || DEFAULT_CURRENCY;

  return (
    <div className="bg-light rounded p-4 mb-2">
      <div className="fw-bold mb-2">{t('salaryChange')}</div>
      <div className="text-break">
        <strong>
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: newValueCurrency,
          }).format(salaryChange.newValue)}
          {salaryChange.newFrequency && (
            <span className="text-muted">
              {' '}
              / {formatFrequencyForDisplay(salaryChange.newFrequency, t)}
            </span>
          )}
        </strong>
        {salaryChange.effectiveDate && (
          <div className="text-muted small">
            {t('effectiveDate')}
            : <FormattedDate day={salaryChange.effectiveDate} format="short" />
            {salaryChange.endDate && (
              <>
                {' - '}
                <FormattedDate day={salaryChange.endDate} format="short" />
              </>
            )}
          </div>
        )}
        {salaryChange.notes && (
          <div className="text-muted small mt-1">
            {t('notes')}: {salaryChange.notes}
          </div>
        )}
      </div>
      {salaryChange.isQuickUpdate && displayPreviousSalary ? (
        <div className="text-muted text-decoration-line-through text-break">
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: newValueCurrency,
          }).format(displayPreviousSalary?.amount)}
          <span className="text-muted">
            {' '}
            / {formatFrequencyForDisplay(displayPreviousSalary?.frequency, t)}
          </span>
        </div>
      ) : salaryChange.isQuickUpdate !== false &&
        employeeId &&
        salaryChange?.currentSalaryId &&
        salaryData?.manager?.employee?.salaries &&
        salaryData.manager.employee.salaries.length > 0 ? (
        <div className="text-muted text-decoration-line-through text-break">
          Original salary (ID: {salaryChange.currentSalaryId}) - Unable to load
          details
        </div>
      ) : null}
    </div>
  );
};
